import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Calendar, Clock, User, Building2, Mail, Phone, ArrowRight,
  CheckCircle, ChevronLeft, ChevronRight, Loader2
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function OdooBooking({
  verticalId = "",
  verticalName = "",
  buttonText = "Schedule a Demo",
  buttonClassName = "",
  buttonVariant = "default",
  buttonSize = "default",
}) {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1); // 1=form, 2=date/time, 3=confirm
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [config, setConfig] = useState(null);

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", notes: ""
  });

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    api.get("/odoo/config-status").then(r => setConfig(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSlots = async (date) => {
    setLoading(true);
    setSelectedTime("");
    try {
      const res = await api.get(`/odoo/available-slots/${date}`);
      setSlots(res.data.slots || []);
    } catch {
      setSlots([]);
      toast.error("Failed to load available times");
    }
    setLoading(false);
  };

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    loadSlots(dateStr);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Please fill in name and email");
      return;
    }
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/odoo/book", {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        date: selectedDate,
        time: selectedTime,
        vertical_name: verticalName,
        notes: formData.notes,
      });
      setConfirmed(true);
      setStep(3);
      toast.success("Demo scheduled successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
    }
    setSubmitting(false);
  };

  const reset = () => {
    setStep(1);
    setConfirmed(false);
    setFormData({ name: "", email: "", phone: "", company: "", notes: "" });
    setSelectedDate("");
    setSelectedTime("");
    setSlots([]);
    setShowModal(false);
  };

  // Calendar helpers
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthName = currentMonth.toLocaleString("default", { month: "long", year: "numeric" });

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  const isDateDisabled = (day) => {
    if (!day) return true;
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dateObj = new Date(year, month, day);
    const dayOfWeek = dateObj.getDay();
    return dateStr < todayStr || dayOfWeek === 0 || dayOfWeek === 6;
  };

  const formatDateStr = (day) => `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  if (!config?.configured) {
    return (
      <Button
        variant={buttonVariant}
        size={buttonSize}
        className={buttonClassName || "bg-[#0066CC] hover:bg-[#0052A3] gap-2"}
        onClick={() => toast.info("Booking system is being configured. Please contact us directly.")}
        data-testid="booking-btn-unconfigured"
      >
        <Calendar className="h-4 w-4" />
        {buttonText}
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        variant={buttonVariant}
        size={buttonSize}
        className={buttonClassName || "bg-[#0066CC] hover:bg-[#0052A3] gap-2"}
        data-testid="odoo-booking-btn"
      >
        <Calendar className="h-4 w-4" />
        {buttonText}
      </Button>

      <Dialog open={showModal} onOpenChange={(open) => { if (!open) reset(); }}>
        <DialogContent className="max-w-[800px] p-0 overflow-hidden" data-testid="odoo-booking-modal">
          <DialogHeader className="sr-only">
            <DialogTitle>Schedule a Demo</DialogTitle>
            <DialogDescription>Book a personalized demo session</DialogDescription>
          </DialogHeader>

          {/* Step 3: Confirmation */}
          {confirmed ? (
            <div className="text-center py-14 px-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-9 h-9 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Demo Scheduled!</h3>
              <p className="text-slate-600 mb-1">
                {selectedDate && new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                {" at "}{selectedTime}
              </p>
              <p className="text-sm text-slate-500 mb-6">A confirmation will be sent to {formData.email}</p>
              <Button onClick={reset} className="bg-[#0066CC] hover:bg-[#0052A3]">Close</Button>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row min-h-[480px]">
              {/* Left: Form or Calendar */}
              <div className="flex-1 p-6">
                {step === 1 ? (
                  /* Step 1: Contact Info */
                  <div>
                    <div className="flex items-center gap-2.5 mb-5">
                      <div className="w-8 h-8 rounded-lg bg-[#0066CC]/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-[#0066CC]" />
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-slate-900">Your Information</h2>
                        <p className="text-xs text-slate-400">Step 1 of 2</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Full Name *</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              placeholder="Your name" className="pl-10 h-10 text-sm" data-testid="odoo-name" />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Email *</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              placeholder="you@company.com" className="pl-10 h-10 text-sm" data-testid="odoo-email" />
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Phone</Label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              placeholder="+91 98765 43210" className="pl-10 h-10 text-sm" data-testid="odoo-phone" />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Company</Label>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                              placeholder="Company name" className="pl-10 h-10 text-sm" data-testid="odoo-company" />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Notes (optional)</Label>
                        <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          placeholder="Any specific topics you'd like to discuss..." rows={2} className="text-sm resize-none" data-testid="odoo-notes" />
                      </div>
                      <Button
                        onClick={() => {
                          if (!formData.name || !formData.email) { toast.error("Name and email are required"); return; }
                          setStep(2);
                        }}
                        className="w-full h-10 bg-[#0066CC] hover:bg-[#0052A3] text-sm font-semibold mt-2"
                        data-testid="odoo-next-step"
                      >
                        Select Date & Time <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Step 2: Date & Time Picker */
                  <div>
                    <div className="flex items-center gap-2.5 mb-4">
                      <button onClick={() => setStep(1)} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                        <ChevronLeft className="h-4 w-4 text-slate-600" />
                      </button>
                      <div>
                        <h2 className="text-base font-bold text-slate-900">Pick a Date & Time</h2>
                        <p className="text-xs text-slate-400">Step 2 of 2 &middot; {config?.default_duration || 30} min session</p>
                      </div>
                    </div>

                    {/* Mini Calendar */}
                    <div className="border rounded-xl p-3 mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <button onClick={() => setCurrentMonth(new Date(year, month - 1))} className="p-1 hover:bg-slate-100 rounded">
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-sm font-semibold text-slate-800">{monthName}</span>
                        <button onClick={() => setCurrentMonth(new Date(year, month + 1))} className="p-1 hover:bg-slate-100 rounded">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center">
                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
                          <div key={d} className="text-[10px] font-semibold text-slate-400 py-1">{d}</div>
                        ))}
                        {calendarDays.map((day, i) => {
                          if (!day) return <div key={`e-${i}`} />;
                          const ds = formatDateStr(day);
                          const disabled = isDateDisabled(day);
                          const selected = ds === selectedDate;
                          return (
                            <button
                              key={ds}
                              disabled={disabled}
                              onClick={() => handleDateSelect(ds)}
                              className={`text-xs py-1.5 rounded-lg transition-all ${
                                selected ? "bg-[#0066CC] text-white font-bold" :
                                disabled ? "text-slate-300 cursor-not-allowed" :
                                "text-slate-700 hover:bg-blue-50 hover:text-[#0066CC] font-medium"
                              }`}
                              data-testid={`cal-day-${ds}`}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Time Slots */}
                    {selectedDate && (
                      <div>
                        <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          Available times for {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                        </p>
                        {loading ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                          </div>
                        ) : slots.filter(s => s.available).length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-4">No available slots for this date</p>
                        ) : (
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[160px] overflow-y-auto" data-testid="time-slots">
                            {slots.filter(s => s.available).map(s => (
                              <button
                                key={s.time}
                                onClick={() => setSelectedTime(s.time)}
                                className={`text-xs py-2 px-2 rounded-lg border transition-all font-medium ${
                                  selectedTime === s.time
                                    ? "bg-[#0066CC] text-white border-[#0066CC]"
                                    : "border-slate-200 text-slate-700 hover:border-[#0066CC] hover:text-[#0066CC]"
                                }`}
                                data-testid={`time-slot-${s.time}`}
                              >
                                {s.time}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {selectedTime && (
                      <Button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="w-full h-10 bg-[#0066CC] hover:bg-[#0052A3] text-sm font-semibold mt-4"
                        data-testid="odoo-confirm-booking"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                        Confirm Booking
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Right: Summary Panel */}
              <div className="md:w-[260px] bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100 p-5 flex flex-col gap-4">
                <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm">
                  <h4 className="font-bold text-[#0066CC] text-sm mb-3">What to Expect</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2.5">
                      <div className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Clock className="w-3 h-3 text-[#0066CC]" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">{config?.default_duration || 30}-Min Demo</p>
                        <p className="text-[10px] text-slate-500 leading-relaxed">Personalized session with our team</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <div className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Building2 className="w-3 h-3 text-[#0066CC]" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Industry-Specific</p>
                        <p className="text-[10px] text-slate-500 leading-relaxed">{verticalName ? `Focused on ${verticalName}` : "Solutions for your industry"}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <div className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 text-[#0066CC]" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Custom Roadmap</p>
                        <p className="text-[10px] text-slate-500 leading-relaxed">Get a tailored implementation plan</p>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Booking Summary */}
                {(formData.name || selectedDate) && (
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Booking Summary</h4>
                    <div className="space-y-1.5 text-xs">
                      {formData.name && <p className="text-slate-700"><span className="text-slate-400">Name:</span> {formData.name}</p>}
                      {formData.email && <p className="text-slate-700"><span className="text-slate-400">Email:</span> {formData.email}</p>}
                      {selectedDate && (
                        <p className="text-slate-700">
                          <span className="text-slate-400">Date:</span>{" "}
                          {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </p>
                      )}
                      {selectedTime && <p className="text-[#0066CC] font-semibold"><span className="text-slate-400">Time:</span> {selectedTime}</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
